/*
 * Ravniks — core interactions for the brand sections.
 * Vanilla, no dependencies. Works alongside Horizon's own modules (cart drawer,
 * product form) by talking to them through the DOM and their public events.
 */
(function () {
  'use strict';
  const d = document;
  const w = window;
  const $ = (s, r = d) => r.querySelector(s);
  const $$ = (s, r = d) => Array.from(r.querySelectorAll(s));
  const RM = () => w.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const RV = (w.RV = w.RV || {});

  /* ------------------------------------------------------------ reveal */
  let revealIO;
  function initReveal (root = d) {
    const els = $$('[data-rv-reveal], [data-rv-lines]', root).filter((el) => !el.classList.contains('is-in'));
    if (!('IntersectionObserver' in w) || RM()) { els.forEach((el) => el.classList.add('is-in')); return; }
    revealIO = revealIO || new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
    els.forEach((el) => revealIO.observe(el));
  }

  /* --------------------------------------------------- scroll container */
  // Horizon scrolls .page-wrapper (not the window) at 990px and up, so every scroll read/write
  // goes through these helpers.
  function scrollEl () {
    const pw = $('.page-wrapper');
    if (pw) {
      const oy = getComputedStyle(pw).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && pw.scrollHeight > pw.clientHeight + 1) return pw;
    }
    return null;
  }
  function scrollTop () { const el = scrollEl(); return el ? el.scrollTop : w.scrollY; }
  function scrollToY (y, smooth = true) {
    const el = scrollEl();
    const opts = { top: y, behavior: smooth && !RM() ? 'smooth' : 'auto' };
    if (el) el.scrollTo(opts); else w.scrollTo(opts);
  }
  function onAnyScroll (fn) {
    w.addEventListener('scroll', fn, { passive: true });
    const pw = $('.page-wrapper');
    pw && pw.addEventListener('scroll', fn, { passive: true });
    return () => { w.removeEventListener('scroll', fn); pw && pw.removeEventListener('scroll', fn); };
  }
  RV.scrollTop = scrollTop;
  RV.onAnyScroll = onAnyScroll;

  /* ----------------------------------------------------------- anchors */
  function headerOffset () {
    const h = $('.rv-header');
    return h ? h.getBoundingClientRect().height : 0;
  }
  function scrollToEl (el) {
    const y = el.getBoundingClientRect().top + scrollTop() - headerOffset() + 1;
    scrollToY(y);
  }
  RV.scrollToEl = scrollToEl;
  d.addEventListener('click', (e) => {
    const a = e.target.closest('a[href*="#"]');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
    const url = new URL(a.href, location.href);
    if (url.pathname !== location.pathname && url.pathname !== '/' ) return;
    const id = decodeURIComponent(url.hash.slice(1));
    if (!id) return;
    const target = d.getElementById(id);
    if (!target) return;
    e.preventDefault();
    closeMenu();
    scrollToEl(target);
    history.replaceState(null, '', '#' + id);
  });

  /* ------------------------------------------------------------ header */
  function initHeader () {
    const header = $('.rv-header');
    if (!header) return;
    const announce = $('.rv-announce');
    if (announce) {
      d.body.classList.add('rv-has-announce');
      const setA = () => d.body.style.setProperty('--rv-announce-h', announce.offsetHeight + 'px');
      setA(); w.addEventListener('resize', setA, { passive: true });
    }
    let ticking = false;
    const onScroll = () => {
      ticking = false;
      const y = scrollTop();
      header.classList.toggle('is-solid', y > 24 || !header.classList.contains('rv-header--overlay'));
      if (announce) header.style.top = Math.max(0, announce.offsetHeight - y) + 'px';
    };
    onAnyScroll(() => { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } });
    onScroll();

    // scroll-spy
    const links = $$('.rv-header__nav a[href*="#"]', header);
    const map = new Map();
    links.forEach((a) => {
      const id = decodeURIComponent(new URL(a.href, location.href).hash.slice(1));
      const t = id && d.getElementById(id);
      if (t) map.set(t, a);
    });
    if (map.size && 'IntersectionObserver' in w) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const a = map.get(e.target);
          if (e.isIntersecting) { links.forEach((l) => l.classList.remove('is-current')); a.classList.add('is-current'); }
          else a.classList.remove('is-current');
        });
      }, { rootMargin: '-45% 0px -50% 0px' });
      map.forEach((_a, t) => io.observe(t));
    }

    // cart
    $$('[data-rv-cart]').forEach((btn) => btn.addEventListener('click', (e) => {
      const drawer = d.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        e.preventDefault();
        drawer.open();
      }
    }));

    // menu
    $$('[data-rv-menu-open]').forEach((b) => b.addEventListener('click', openMenu));
    $$('[data-rv-menu-close]').forEach((b) => b.addEventListener('click', closeMenu));
    d.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenu(); });
  }
  let lastFocus = null;
  function openMenu () {
    const m = $('.rv-menu');
    if (!m) return;
    lastFocus = d.activeElement;
    m.hidden = false;
    requestAnimationFrame(() => m.classList.add('is-open'));
    m.setAttribute('aria-hidden', 'false');
    d.body.classList.add('rv-lock');
    d.documentElement.setAttribute('scroll-lock', '');
    $$('[data-rv-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const f = $('[data-rv-menu-close]', m); f && f.focus();
  }
  function closeMenu () {
    const m = $('.rv-menu');
    if (!m || !m.classList.contains('is-open')) return;
    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    d.body.classList.remove('rv-lock');
    d.documentElement.removeAttribute('scroll-lock');
    $$('[data-rv-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    lastFocus && lastFocus.focus && lastFocus.focus();
  }

  /* ------------------------------------------------------- cart count */
  function setCount (n) {
    $$('[data-rv-cart-count]').forEach((el) => {
      const prev = +el.textContent || 0;
      el.textContent = n;
      el.hidden = !n;
      if (n > prev) { el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump'); }
    });
  }
  function refreshCount () {
    fetch((w.Shopify && w.Shopify.routes && w.Shopify.routes.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json()).then((c) => setCount(c.item_count || 0)).catch(() => {});
  }
  d.addEventListener('shopify:cart:lines-update', (e) => {
    if (e.promise && e.promise.then) {
      e.promise.then((res) => {
        const n = res && ((res.cart && res.cart.totalQuantity) ?? (res.detail && res.detail.itemCount));
        if (typeof n === 'number') setCount(n); else refreshCount();
      }).catch(() => refreshCount());
    } else setTimeout(refreshCount, 400);
  });
  d.addEventListener('cart:update', () => setTimeout(refreshCount, 50));
  w.addEventListener('pageshow', (e) => { if (e.persisted) refreshCount(); });

  /* ------------------------------------------------------------ videos */
  function initVideos (root = d) {
    const vids = $$('video[data-rv-autoplay], [data-rv-video] video', root);
    if (!vids.length) return;
    if (RM()) { vids.forEach((v) => { v.removeAttribute('autoplay'); v.pause(); }); return; }
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      const v = e.target;
      if (e.isIntersecting) { const p = v.play(); p && p.catch(() => {}); } else v.pause();
    }), { threshold: 0.15 });
    vids.forEach((v) => { v.muted = true; v.playsInline = true; io.observe(v); });
  }

  /* --------------------------------------------- statement highlight */
  function initStatements (root = d) {
    $$('[data-rv-statement]', root).forEach((el) => {
      const words = $$('.rv-word', el);
      if (!words.length) return;
      if (RM()) { words.forEach((wd) => wd.classList.add('is-lit')); return; }
      let raf = 0;
      const update = () => {
        raf = 0;
        const r = el.getBoundingClientRect();
        const vh = w.innerHeight;
        const start = vh * 0.85;
        const end = vh * 0.3;
        const p = Math.min(1, Math.max(0, (start - r.top) / (start - end + r.height * 0.6)));
        const n = Math.round(p * words.length);
        words.forEach((wd, i) => wd.classList.toggle('is-lit', i < n));
      };
      const onS = () => { if (!raf) raf = requestAnimationFrame(update); };
      let off = null;
      const io = new IntersectionObserver((es) => es.forEach((e) => {
        if (e.isIntersecting) { if (!off) off = onAnyScroll(onS); update(); }
        else if (off) { off(); off = null; }
      }));
      io.observe(el);
    });
  }

  /* ---------------------------------------------------------- accordion */
  function initAccordions (root = d) {
    $$('details[data-rv-acc]', root).forEach((det) => {
      if (det._rv) return;
      det._rv = true;
      const sum = $('summary', det);
      const body = $('.rv-acc__body', det);
      if (!sum || !body) return;
      sum.addEventListener('click', (e) => {
        if (RM()) return;
        e.preventDefault();
        if (det.open) {
          const h = body.scrollHeight;
          body.style.height = h + 'px';
          requestAnimationFrame(() => { body.style.height = '0px'; });
          det.classList.remove('is-open');
          body.addEventListener('transitionend', function te () { det.open = false; body.style.height = ''; body.removeEventListener('transitionend', te); });
        } else {
          det.open = true;
          det.classList.add('is-open');
          const h = body.scrollHeight;
          body.style.height = '0px';
          requestAnimationFrame(() => { body.style.height = h + 'px'; });
          body.addEventListener('transitionend', function te () { body.style.height = ''; body.removeEventListener('transitionend', te); });
        }
      });
      if (det.open) det.classList.add('is-open');
    });
  }

  /* ------------------------------------------------------- hero guitar */
  function initHero (root = d) {
    $$('[data-rv-hero]', root).forEach((hero) => {
      const g = $('[data-rv-parallax]', hero);
      if (!g) return;
      // subtle pointer parallax on desktop
      if (RM() || !w.matchMedia('(pointer: fine)').matches) return;
      let raf = 0; let tx = 0; let ty = 0;
      hero.addEventListener('pointermove', (e) => {
        const r = hero.getBoundingClientRect();
        tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
        ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
        if (!raf) raf = requestAnimationFrame(() => {
          raf = 0;
          g.style.transform = `translate3d(${(-tx * 10).toFixed(1)}px, ${(-ty * 6).toFixed(1)}px, 0)`;
        });
      });
      hero.addEventListener('pointerleave', () => { g.style.transform = ''; });
    });
  }

  /* -------------------------------------------------------- sticky bar */
  function initStickyBar () {
    const bar = $('[data-rv-stickybar]');
    if (!bar) return;
    const hero = $('.rv-hero');
    const buy = d.getElementById(bar.dataset.target || 'buy');
    const foot = $('footer');
    let pastHero = false; let buyVisible = false; let footVisible = false;
    // interactive areas (the fretboard demo) keep the bottom of the screen clear
    const avoiding = new Set();
    const avoidIo = new IntersectionObserver((es) => es.forEach((e) => {
      e.isIntersecting ? avoiding.add(e.target) : avoiding.delete(e.target);
      update();
    }), { rootMargin: '-80% 0px 0px 0px', threshold: 0 });
    $$('[data-rv-bar-avoid]').forEach((el) => avoidIo.observe(el));
    const update = () => {
      const show = pastHero && !buyVisible && !footVisible && avoiding.size === 0;
      bar.classList.toggle('is-shown', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.inert = !show;
    };
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.target === hero) pastHero = !e.isIntersecting && e.boundingClientRect.top < 0;
      if (e.target === buy) buyVisible = e.isIntersecting;
      if (e.target === foot) footVisible = e.isIntersecting;
      update();
    }), { threshold: 0 });
    hero ? io.observe(hero) : (pastHero = true);
    buy && io.observe(buy);
    foot && io.observe(foot);
    update();
  }


  /* ---------------------------------------------------------------- buy */
  const SWATCH = { black: '#1f1f1f', white: '#ededed', pink: '#edc2ca', mint: '#dae9d8' };
  function initBuy (root = d) {
    $$('[data-rv-buy]', root).forEach((sec) => {
      if (sec._rv) return;
      sec._rv = true;
      const jsonEl = $('[data-rv-buy-json]', sec);
      if (!jsonEl) return;
      let data;
      try { data = JSON.parse(jsonEl.textContent); } catch (e) { return; }
      const pos = data.optionPosition;
      const stage = $('[data-rv-buy-stage]', sec);
      const guitar = stage && $('rv-guitar', stage);
      const photo = $('[data-rv-buy-photo]', sec);
      const priceEl = $('[data-rv-price]', sec);
      const compareEl = $('[data-rv-compare]', sec);
      const nameEl = $('[data-rv-finish-name]', sec);
      const radios = $$('[data-rv-finish]', sec);
      const bar = $('[data-rv-stickybar]');
      const formWrap = $('[data-rv-buy-form]', sec);
      const termsInput = $('.rv-buy__terms input[name="id"]', sec);
      const getForm = () => formWrap && $('form[data-type="add-to-cart-form"], form', formWrap);
      const getId = () => { const f = getForm(); return f && $('input[name="id"]', f); };
      const getAdd = () => formWrap && $('button[name="add"]', formWrap);
      let current = data.variants.find((v) => String(v.id) === String(getId() && getId().value)) || data.variants[0];
      let activeThumb = 'render';

      const setBtnText = (btn, text) => {
        const tn = Array.from(btn.childNodes).find((n) => n.nodeType === 3 && n.textContent.trim());
        if (tn) tn.textContent = text; else btn.insertBefore(d.createTextNode(text), btn.querySelector('.add-to-cart__added'));
      };

      function swapRender (key) {
        if (!guitar || guitar.dataset.color === key || !data.renders[key]) return;
        const img = $('img', guitar);
        stage.classList.add('is-swapping');
        const pre = new Image();
        pre.src = data.renders[key].v1200;
        const done = () => {
          img.srcset = `${data.renders[key].v600} 600w, ${data.renders[key].v1200} 1200w`;
          img.src = data.renders[key].v1200;
          guitar.dataset.color = key;
          requestAnimationFrame(() => stage.classList.remove('is-swapping'));
        };
        setTimeout(() => (pre.complete ? done() : (pre.onload = pre.onerror = done)), 220);
      }

      function apply (v, fromUser) {
        if (!v) return;
        current = v;
        const idIn = getId();
        if (idIn && String(idIn.value) !== String(v.id)) {
          idIn.value = v.id;
          idIn.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (termsInput) termsInput.value = v.id;
        const btn = getAdd();
        if (btn) {
          btn.disabled = !v.available;
          setBtnText(btn, v.available ? data.strings.add : data.strings.soldOut);
        }
        if (priceEl) priceEl.textContent = v.price;
        if (compareEl) { compareEl.textContent = v.compare; compareEl.hidden = !v.compare; }
        if (nameEl) nameEl.textContent = v.finish || v.title;
        swapRender(v.key);
        const lt = $('[data-rv-life-thumb]', sec);
        if (lt && data.life && data.life[v.key]) lt.src = data.life[v.key].s;
        if (activeThumb === 'life') showLife(); else showRender();
        if (bar) {
          const sw = $('[data-rv-bar-swatch]', bar);
          sw && sw.style.setProperty('--sw', SWATCH[v.key] || '#1f1f1f');
          const bv = $('[data-rv-bar-variant]', bar); bv && (bv.textContent = v.finish || v.title);
          const bp = $('[data-rv-bar-price]', bar); bp && (bp.textContent = v.price);
          const bb = $('[data-rv-bar-add]', bar); bb && bb.toggleAttribute('aria-disabled', !v.available);
        }
        if (fromUser && data.isProductPage && history.replaceState) {
          const u = new URL(location.href);
          u.searchParams.set('variant', v.id);
          history.replaceState(history.state, '', u.toString());
        }
        d.dispatchEvent(new CustomEvent('rv:variant', { detail: v }));
      }

      function variantFor (value) {
        const others = current ? current.options.slice() : [];
        let v = data.variants.find((x) => x.options[pos - 1] === value && x.options.every((o, i) => i === pos - 1 || o === others[i]));
        if (!v || !v.available) v = data.variants.find((x) => x.options[pos - 1] === value && x.available) || v || data.variants.find((x) => x.options[pos - 1] === value);
        return v;
      }

      radios.forEach((r) => r.addEventListener('change', () => { if (r.checked) apply(variantFor(r.value), true); }));

      // finishes section → buy
      d.addEventListener('rv:pick-color', (e) => {
        const { key, value } = e.detail || {};
        let v = data.variants.find((x) => value && (x.finish || '').toLowerCase() === String(value).toLowerCase());
        if (!v) v = data.variants.find((x) => x.key === key && x.available) || data.variants.find((x) => x.key === key);
        if (v) {
          const r = radios.find((x) => x.value === v.finish);
          if (r) r.checked = true;
          apply(v, true);
        } else swapRender(key);
      });

      // gallery thumbnails
      const thumbs = $$('[data-rv-thumb]', sec);
      const markThumb = (name) => { activeThumb = name; thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.rvThumb === name)); };
      function showRender () {
        markThumb('render');
        if (!photo || photo.hidden) return;
        photo.hidden = true;
        photo.innerHTML = '';
      }
      function showPhoto (src, alt, srcset, cls) {
        if (!photo) return;
        const img = d.createElement('img');
        if (srcset) { img.srcset = srcset; img.sizes = '(max-width: 989px) 100vw, 50vw'; }
        img.src = src;
        img.alt = alt || '';
        img.decoding = 'async';
        if (cls) img.className = cls;
        photo.innerHTML = '';
        photo.appendChild(img);
        photo.hidden = false;
      }
      function showLife () {
        const l = data.life && current && data.life[current.key];
        if (!l) { showRender(); return; }
        markThumb('life');
        showPhoto(l.m, (data.lifeAlt || '') + (current.finish ? ' — ' + current.finish : ''), `${l.s} 640w, ${l.m} 960w, ${l.l} 1440w`, 'is-life');
      }
      thumbs.forEach((t) => t.addEventListener('click', () => {
        const name = t.dataset.rvThumb;
        if (name === 'render') { showRender(); return; }
        if (name === 'life') { showLife(); return; }
        markThumb(name);
        showPhoto(name, t.dataset.alt);
      }));

      // sticky bar → add to cart
      if (bar) {
        const bb = $('[data-rv-bar-add]', bar);
        bb && bb.addEventListener('click', () => {
          const btn = getAdd();
          if (btn && !btn.disabled) btn.click();
          else scrollToEl(sec);
        });
      }

      // keep in sync if Horizon (or the URL) changes the variant id
      const idIn = getId();
      idIn && idIn.addEventListener('change', () => {
        const v = data.variants.find((x) => String(x.id) === String(idIn.value));
        if (v && v !== current) apply(v);
      });
      apply(current, false);
    });
  }

  /* ----------------------------------------------------------- finishes */
  function initColors (root = d) {
    $$('[data-rv-colors]', root).forEach((sec) => {
      if (sec._rv) return;
      const opts = $$('[data-rv-opt]', sec);
      const shots = $$('[data-rv-shot]', sec);
      if (!opts.length || !shots.length) return;
      sec._rv = true;
      const photo = $('.rv-colors__photo', sec);
      const count = $('[data-rv-colors-count]', sec);
      const capName = $('[data-rv-colors-capname]', sec);
      const choose = $('[data-rv-colors-choose]', sec);
      const chooseLabel = choose && $('[data-rv-colors-choose-label]', choose);
      const interval = Math.max(2, parseFloat(sec.dataset.interval) || 3.6) * 1000;
      sec.style.setProperty('--rv-interval', interval + 'ms');
      let cur = 0; let timer = 0; let auto = !RM(); let inView = false; let paintT = 0;
      const pad = (n) => String(n).padStart(2, '0');

      const show = (i, { user = false, focus = false } = {}) => {
        i = (i + opts.length) % opts.length;
        const same = i === cur;
        const btn = opts[i];
        opts.forEach((o, k) => { o.setAttribute('aria-checked', k === i ? 'true' : 'false'); o.tabIndex = k === i ? 0 : -1; });
        if (focus) btn.focus();
        if (count) count.textContent = pad(i + 1);
        if (capName) capName.textContent = btn.dataset.value;
        if (chooseLabel) chooseLabel.textContent = (choose.dataset.label ? choose.dataset.label + ' ' : '') + btn.dataset.value;
        if (!same) {
          const prev = shots[cur]; const next = shots[i];
          shots.forEach((s) => { if (s !== prev && s !== next) s.classList.remove('is-active', 'is-prev', 'is-entering'); });
          prev.classList.remove('is-active', 'is-entering'); prev.classList.add('is-prev');
          next.classList.remove('is-prev', 'is-entering');
          void next.offsetWidth;
          next.classList.add('is-active');
          if (!RM()) {
            next.classList.add('is-entering');
            if (photo) {
              photo.style.setProperty('--scan-w', photo.offsetWidth + 'px');
              photo.classList.remove('is-painting'); void photo.offsetWidth; photo.classList.add('is-painting');
            }
            clearTimeout(paintT);
            paintT = setTimeout(() => { prev.classList.remove('is-prev'); next.classList.remove('is-entering'); photo && photo.classList.remove('is-painting'); }, 1100);
          } else prev.classList.remove('is-prev');
          cur = i;
        }
        if (user) {
          stopAuto();
          // keep the buy section in step with what the visitor is looking at
          d.dispatchEvent(new CustomEvent('rv:pick-color', { detail: { key: btn.dataset.key, value: btn.dataset.value, silent: true } }));
        } else if (auto) restartBar();
      };
      const restartBar = () => {
        const bar = $('.rv-colors__bar', opts[cur]);
        if (!bar) return;
        bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = '';
      };
      const tick = () => { show(cur + 1); schedule(); };
      const schedule = () => { clearTimeout(timer); if (auto && inView && !d.hidden) timer = setTimeout(tick, interval); };
      const stopAuto = () => { auto = false; clearTimeout(timer); sec.classList.remove('is-auto'); };
      if (auto) sec.classList.add('is-auto');

      opts.forEach((o, i) => {
        o.addEventListener('click', () => show(i, { user: true }));
        o.addEventListener('keydown', (e) => {
          const k = e.key;
          if (k === 'ArrowDown' || k === 'ArrowRight') { e.preventDefault(); show(i + 1, { user: true, focus: true }); }
          if (k === 'ArrowUp' || k === 'ArrowLeft') { e.preventDefault(); show(i - 1, { user: true, focus: true }); }
          if (k === 'Home') { e.preventDefault(); show(0, { user: true, focus: true }); }
          if (k === 'End') { e.preventDefault(); show(opts.length - 1, { user: true, focus: true }); }
        });
      });
      // hovering the list previews without committing; leaving resumes the cycle
      const list = $('.rv-colors__list', sec);
      if (list && w.matchMedia('(hover: hover)').matches) {
        opts.forEach((o, i) => o.addEventListener('pointerenter', () => { if (auto) { clearTimeout(timer); sec.classList.remove('is-auto'); show(i); } }));
        list.addEventListener('pointerleave', () => { if (auto) { sec.classList.add('is-auto'); restartBar(); schedule(); } });
      }
      if (choose) choose.addEventListener('click', () => {
        const btn = opts[cur];
        stopAuto();
        d.dispatchEvent(new CustomEvent('rv:pick-color', { detail: { key: btn.dataset.key, value: btn.dataset.value } }));
        const buy = d.getElementById('buy') || $('[data-rv-buy]');
        if (buy) scrollToEl(buy);
      });
      new IntersectionObserver((es) => es.forEach((e) => {
        inView = e.isIntersecting;
        if (inView && auto) { restartBar(); schedule(); } else clearTimeout(timer);
      }), { threshold: 0.35 }).observe(photo || sec);
      d.addEventListener('visibilitychange', schedule);
    });
  }

  /* ------------------------------------------------------------ anatomy */
  function initAnatomy (root = d) {
    $$('[data-rv-anatomy]', root).forEach((sec) => {
      if (sec._rv) return;
      sec._rv = true;
      const pins = $$('[data-rv-pin]', sec);
      $$('[data-rv-pin-target]', sec).forEach((item) => {
        const n = item.dataset.rvPinTarget;
        const act = (on) => {
          item.classList.toggle('is-active', on);
          pins.forEach((p) => p.classList.toggle('is-active', on && p.dataset.rvPin === n));
        };
        item.addEventListener('pointerenter', () => act(true));
        item.addEventListener('pointerleave', () => act(false));
        item.addEventListener('focus', () => act(true));
        item.addEventListener('blur', () => act(false));
      });
    });
  }

  RV.sections = Object.assign(RV.sections || {}, { buy: initBuy, colors: initColors, anatomy: initAnatomy });

  /* -------------------------------------------------------------- boot */
  function boot (root) {
    initReveal(root);
    initVideos(root);
    initStatements(root);
    initAccordions(root);
    initHero(root);
    RV.sections && Object.values(RV.sections).forEach((fn) => { try { fn(root); } catch (err) { console.error(err); } });
  }
  RV.boot = boot;

  function start () {
    initHeader();
    initStickyBar();
    boot(d);
    refreshCount();
  }
  if (d.readyState === 'complete') start(); else d.addEventListener('DOMContentLoaded', start);

  // Theme editor support
  d.addEventListener('shopify:section:load', (e) => boot(e.target));
  d.addEventListener('shopify:block:select', (e) => { const el = e.target; el && el.classList && el.classList.add('is-in'); });
})();
